import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    ClickAwayListener,
    IconButton,
    Paper,
    Popper,
    styled
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UndoIcon from '@mui/icons-material/Undo';
import Colors from 'constants/colors';
import useFilterStore from 'zustandStore/useFilterStore';
import { UPDATE } from 'ducks/filters';
import { isTestEnvironment } from 'lib/Environment';
import RpClearFiltersModal from 'components/commons/RpClearFiltersModal';
import parseInt from 'lodash/parseInt';
import { trackRefineSearch, trackResetFiltersEvent } from 'lib/MixpanelUtil';

const StyledButton = styled(Button)({
    fontWeight: 500,
    width: 100,
    height: 38
});

function FilterActions(props) {
    const [popperOpen, setPopperOpen] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const iconButtonRef = useRef(null);

    const dispatch = useDispatch();

    const zustandStore = useFilterStore(state => state);

    const { propertyId } = useParams();
    const parsedPropertyId = useMemo(
        () => parseInt(propertyId, 10),
        [propertyId]
    );
    const handleIconClick = () => setPopperOpen((open) => !open);

    const handleClearAllClick = () => {
        setPopperOpen(false);
        setModalOpen(true);
    };

    const handleModalClose = () => setModalOpen(false);

    const handleConfirmReset = () => {
        setModalOpen(false);
        if (zustandStore.isAppliedFiltersDirty(props.comparableType)) {
            // this is a workaround to force re-fetching data and make filters dirty
            // see line 241 of src/actionCreators/comparableItems.js
            zustandStore.resetFilters(props.comparableType);
            dispatch({ type: UPDATE, payload: {} });
            props.applyFilter();
            trackResetFiltersEvent(props.comparableType, propertyId);
        } else {
            zustandStore.resetFilters(props.comparableType);
            trackResetFiltersEvent(props.comparableType, propertyId);
        }

    };

    const onApplyFilters = () => {
        // this is a workaround to force re-fetching data and make filters dirty
        // see line 241 of src/actionCreators/comparableItems.js
        dispatch({ type: UPDATE, payload: {} });
        zustandStore.applyFilters(props.comparableType);
        props.applyFilter();
        trackRefineSearch(props.comparableType, parsedPropertyId, zustandStore.filters);
    };

    const disabledApplyFilter = !zustandStore.hasAppliedFilterChanged(props.comparableType)
        || zustandStore.errors[props.comparableType]?.length;

    return (
        <Box sx={{display: 'flex', alignItems: 'center'}}>
            <StyledButton
                variant="contained"
                color="primary"
                onClick={disabledApplyFilter ? () => {} : onApplyFilters}
                disabled={disabledApplyFilter}
                sx={{
                    '&.Mui-disabled': {
                        color: Colors.WHITE,
                    }
                }}
            >
                {props.action || 'Search'}
            </StyledButton>
            {props.withReset && (
                <>
                    <IconButton
                        ref={iconButtonRef}
                        onClick={handleIconClick}
                        size="small"
                        data-testid="filter-actions-icon-button"
                    >
                        <MoreVertIcon sx={{fontSize: '22.28px'}}/>
                    </IconButton>
                    <Popper
                        open={popperOpen}
                        anchorEl={iconButtonRef.current}
                        placement="bottom-end"
                        style={{zIndex: 1300}}
                        disablePortal={isTestEnvironment}
                    >
                        <ClickAwayListener onClickAway={() => setPopperOpen(false)}>
                            <Paper sx={{mt: 1}}>
                                <StyledButton
                                    variant="text"
                                    color="secondary"
                                    onClick={handleClearAllClick}
                                    startIcon={<UndoIcon sx={{color: Colors.ERROR.TEXT}}/>}
                                    sx={{
                                        width: 110,
                                        color: Colors.BLACK_60_OPAQUE,
                                        '&.Mui-disabled': {
                                            '& .MuiButton-startIcon': {
                                                opacity: 0.4
                                            }
                                        }
                                    }}
                                    disabled={!zustandStore.isFiltersDirty(props.comparableType)}
                                >
                                    Clear all
                                </StyledButton>
                            </Paper>
                        </ClickAwayListener>
                    </Popper>
                    <RpClearFiltersModal
                        open={modalOpen}
                        onClose={handleModalClose}
                        onConfirm={handleConfirmReset}
                    />
                </>
            )}
        </Box>
    );
}

FilterActions.propTypes = {
    comparableType: PropTypes.string.isRequired,
    applyFilter: PropTypes.func,
    action: PropTypes.string,
    withReset: PropTypes.bool
};

export default FilterActions;